import { createFeatureSelector, createSelector } from '@ngrx/store';
import { Helper } from '@red/utils';
import { EClientType } from '@shared/data-access/enums';
import { ResaleTransactionReducer } from '../reducers';
import { PAYMENT_ADVICE_OPTIONS } from '@shared/data-access/data';
import { EcddModel } from '@shared/data-access/models';

export const selectResaleTransactionFeature = createFeatureSelector<ResaleTransactionReducer.ResaleTransactionState>(ResaleTransactionReducer.featureName);

export const selectItemDetail = createSelector(selectResaleTransactionFeature, ({ item }) => item);

export const selectResaleTransactionId = createSelector(selectResaleTransactionFeature, ({ item }) => item?.id);

export const selectDraftItem = createSelector(selectResaleTransactionFeature, ({ draftItem }) => draftItem);

export const selectDraftType = createSelector(selectResaleTransactionFeature, ({ draftType }) => draftType);

export const selectDraftState = createSelector(selectResaleTransactionFeature, ({ draftItem, draftType }) => ({
  draftItem,
  draftType,
}));

export const selectSubmittingStatus = createSelector(selectResaleTransactionFeature, ({ submittingStatus }) => submittingStatus);

export const selectTransactionAmountDraftItem = createSelector(selectDraftItem, ({ billing }) => billing?.transactionAmount as number | null);

export const selectTotalExternalsReceivedPercentDraftItem = createSelector(selectDraftItem, ({ relatedAgent }) =>
  relatedAgent.externals.reduce((total, item) => Helper.round(total + (item?.receivedPercent ?? 0), 4), 0)
);

export const selectBillingValueDraftItem = createSelector(selectDraftItem, ({ billing }) => billing?.grossComm?.subTotalAmount ?? 0);

export const selectedBillingGstPercent = createSelector(selectDraftItem, ({ billing }) => billing?.gstPercent ?? 0);

export const selectPropertyAddressDraftItem = createSelector(selectDraftItem, ({ propertyAddress }) => propertyAddress);

export const selectCountryDraftItem = createSelector(selectDraftItem, ({ country }) => country);

export const selectPropertyAddressFullDraftItem = createSelector(selectPropertyAddressDraftItem, selectCountryDraftItem, (propertyAddress, country) => ({
  ...propertyAddress,
  country,
}));

export const selectResaleTypeDraftItem = createSelector(selectDraftItem, ({ resaleType }) => resaleType);

export const selectOptionsPaymentAdviceDraftItem = createSelector(
  selectResaleTypeDraftItem,
  (resaleType) => Object.values(PAYMENT_ADVICE_OPTIONS).filter(
    option => option['resaleTypesApply']?.includes(resaleType) && option['showInPortal'])
);

export const selectBillingMetadataDraftItem = createSelector(selectDraftItem, ({ billing }) => billing?.metadata);

export const selectIsCobrokeDraftItem = createSelector(selectDraftItem, ({ metadata }) => !!metadata?.isCobroke);

export const selectIsWithPaymentDraftItem = createSelector(selectDraftItem, ({ metadata }) => !!metadata?.isWithPayment);

export const selectSubAmountDraftItem = createSelector(selectDraftItem, ({ billing }) => billing?.subAmount ?? null);

export const selectSubTotalAmountDraftItem = createSelector(selectDraftItem, ({ billing }) => billing?.grossComm?.subTotalAmount ?? null);

export const selectDocumentsDraftItem = createSelector(selectDraftItem, ({ documents }) => documents);

export const selectClientType = createSelector(selectDraftItem, ({ billing }) => billing.clientType ?? EClientType.seller);

export const selectPropertyTypeDraftItem = createSelector(selectDraftItem, ({ propertyType }) => propertyType);

export const selectPaymentAdviceDraftItem = createSelector(selectDraftItem, ({ billing }) => billing.paymentAdvice);

export const selectBillingDraftItem = createSelector(selectDraftItem, ({ billing }) => billing);

export const selectEcddsBilling = createSelector(selectDraftItem, ({ billing, relatedAgent }) => {
  return {
    ecddsCA: billing?.ecddsCA ?? { ecdds: relatedAgent?.mains?.[0]?.ecdds as EcddModel[], isECDD: relatedAgent?.mains?.[0]?.attrs?.['isECDD'] },
    ecddsICBs: billing?.ecddsICBs
  };
});

export const selectRelatedAgentDraftItem = createSelector(selectDraftItem, ({ relatedAgent }) => relatedAgent);

export const selectBillingContactDraftItem = createSelector(selectBillingDraftItem, ({ contact }) => contact);

export const selectIsCloneDuplicated = createSelector(selectResaleTransactionFeature, ({ isCloneDuplicated }) => isCloneDuplicated);

export const selectStatusGrossCommPreview = createSelector(selectResaleTransactionFeature, ({ previewStatus }) => previewStatus);

export const selectMains = createSelector(selectDraftItem, ({ relatedAgent }) => relatedAgent?.mains);

export const selectMainId = createSelector(selectMains, (mains) => mains?.[0]?.agentId);

export const selectInternals = createSelector(selectRelatedAgentDraftItem, ({ internals }) => internals);

export const selectPropertyAddressFields = createSelector(
  selectPropertyAddressDraftItem,
  (propertyAddress) => ({
    country: propertyAddress?.country,
    postal: propertyAddress?.postal,
    block: propertyAddress?.block,
    street: propertyAddress?.street,
    unit: propertyAddress?.unit,
    floor: propertyAddress?.floor,
    isFullAddress: !!(propertyAddress?.country && propertyAddress?.postal && propertyAddress?.block && propertyAddress?.street),
  })
);

export const selectSubmissionDate = createSelector(selectDraftItem, ({ submissionDate }) => submissionDate);
